/**
 * Los grupos de mail salen por Snov (fase 4): lo manda quien maneja Snov y Activa le arma lo que necesita. Por grupo,
 * «Bajar la lista para Snov» (los pendientes del padrón del envío, nunca el control) y «Ver el HTML para Snov» (el
 * mail de Mensajes con el cupón puesto y las variables de Snov). Para el envío, «Vincular la campaña de Snov»: con la
 * sincronización de Emails, quien la recibió queda enviado en el padrón con su hora.
 */

import { useEffect, useState } from "react";
import { Copy, Download, Link2, Loader2 } from "lucide-react";
import { num, plural } from "../../campana-nueva/utils/format";
import { btnSecundario } from "../../segmentaciones/components/PasosCampana";
import { leerError, leerRespuesta, textoError } from "../../wa-oficial/logic/respuesta";
import { enviosApi } from "../api";
import type { HtmlParaSnov, ListaParaSnov, SnovDelEnvio as Snov } from "../types";

/** Baja un texto como archivo (sin abrir otra pestaña). */
function bajarArchivo(nombre: string, contenido: string): boolean {
  try {
    const url = URL.createObjectURL(new Blob([contenido], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = nombre;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // ya no importa
      }
    }, 1000);
    return true;
  } catch {
    return false;
  }
}

/** «3 personas en la lista.» más lo que conviene saber antes de importarla. */
export function textoDeLaLista(l: ListaParaSnov): string {
  const partes = [`${num(l.personas)} ${plural(l.personas, "persona", "personas")} en la lista.`];
  if (l.bajasNuevas > 0) partes.push(`${num(l.bajasNuevas)} ${plural(l.bajasNuevas, "se dio", "se dieron")} de baja desde que preparaste: ${plural(l.bajasNuevas, "quedó", "quedaron")} afuera.`);
  if (l.sinNombre > 0) partes.push(`${num(l.sinNombre)} sin nombre: si el texto saluda con {nombre}, ${plural(l.sinNombre, "le llega", "les llega")} «Hola ,».`);
  return partes.join(" ");
}

export function SnovDelEnvio({ segmentacionId, grupos, alVincular }: { segmentacionId: string; grupos: { id: string; nombre: string }[]; alVincular?: () => void }) {
  const [listas, setListas] = useState<Record<string, ListaParaSnov>>({});
  const [htmls, setHtmls] = useState<Record<string, HtmlParaSnov>>({});
  const [ocupado, setOcupado] = useState("");
  const [error, setError] = useState("");
  const [snov, setSnov] = useState<Snov | null>(null);
  const [elegidas, setElegidas] = useState<number[]>([]);
  const [guardado, setGuardado] = useState(false);

  useEffect(() => {
    let vivo = true;
    enviosApi
      .snov(segmentacionId)
      .then((r) => {
        const l = leerRespuesta<Snov>(r);
        if (!vivo || !l.ok) return;
        setSnov(l.data);
        setElegidas(l.data.vinculadas);
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [segmentacionId]);

  const correr = async <D,>(clave: string, fn: () => Promise<unknown>, alTerminar: (d: D) => void) => {
    setOcupado(clave);
    setError("");
    try {
      const l = leerRespuesta<D>(await fn());
      if (!l.ok) return setError(textoError(l));
      alTerminar(l.data);
    } catch (e) {
      setError(textoError(leerError(e)));
    } finally {
      setOcupado("");
    }
  };

  const bajar = (g: string) =>
    correr<ListaParaSnov>(`lista-${g}`, () => enviosApi.listaSnov(segmentacionId, g), (l) => {
      setListas((x) => ({ ...x, [g]: l }));
      if (!bajarArchivo(l.archivo, l.csv)) setError("No pudimos armar el archivo en este navegador. Probá con otro.");
    });
  const verHtml = (g: string) => correr<HtmlParaSnov>(`html-${g}`, () => enviosApi.htmlSnov(segmentacionId, g), (h) => setHtmls((x) => ({ ...x, [g]: h })));
  const vincular = () =>
    correr<Snov>("vincular", () => enviosApi.vincularSnov(segmentacionId, elegidas), (s) => {
      setSnov(s);
      setElegidas(s.vinculadas);
      setGuardado(true);
      alVincular?.();
    });
  const copiar = (texto: string) => {
    void navigator.clipboard?.writeText(texto).catch(() => undefined);
  };
  const alternar = (id: number) => {
    setGuardado(false);
    setElegidas((x) => (x.includes(id) ? x.filter((y) => y !== id) : [...x, id]));
  };
  const cambio = !!snov && [...elegidas].sort().join(",") !== [...snov.vinculadas].sort().join(",");

  return (
    <section className="w-full space-y-3 rounded-xl border border-sky-200 bg-sky-50/60 p-4 text-sm text-slate-700" data-testid="envio-snov">
      <p className="font-semibold text-slate-900">Por mail, por Snov</p>
      <p className="text-[13px] text-slate-600" data-testid="snov-antes">
        Antes de importar la lista, creá en Snov los campos «sucursal» y «dias_sin_comprar» (Snov no deja crearlos desde acá). Al crear la campaña, prendé la baja en un clic. La lista nunca
        trae al grupo de control.
      </p>
      {grupos.map((g) => {
        const l = listas[g.id];
        const h = htmls[g.id];
        return (
          <div key={g.id} className="space-y-2" data-testid={`snov-grupo-${g.id}`}>
            <p className="font-semibold text-slate-900">{g.nombre}</p>
            <div className="flex flex-wrap gap-2">
              <button type="button" className={btnSecundario} disabled={!!ocupado} onClick={() => void bajar(g.id)}>
                {ocupado === `lista-${g.id}` ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Download size={15} aria-hidden="true" />}
                Bajar la lista para Snov
              </button>
              <button type="button" className={btnSecundario} disabled={!!ocupado} onClick={() => void verHtml(g.id)}>
                {ocupado === `html-${g.id}` ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
                Ver el HTML para Snov
              </button>
            </div>
            {l && <p data-testid={`snov-lista-${g.id}`}>{textoDeLaLista(l)}</p>}
            {h && (
              <div className="space-y-1.5" data-testid={`snov-html-${g.id}`}>
                <p>
                  Asunto: <span className="font-mono">{h.asunto}</span>
                </p>
                <textarea readOnly aria-label={`El HTML de ${g.nombre} para Snov`} value={h.html} rows={5} className="w-full rounded-lg border border-slate-200 bg-white p-2 font-mono text-xs" />
                <button type="button" className={btnSecundario} onClick={() => copiar(h.html)}>
                  <Copy size={15} aria-hidden="true" />
                  Copiar el HTML
                </button>
              </div>
            )}
          </div>
        );
      })}
      {snov && (
        <div className="space-y-2 border-t border-sky-200 pt-3" data-testid="snov-vincular">
          <p className="font-semibold text-slate-900">¿Qué campaña de Snov lo manda?</p>
          {snov.campanas.length === 0 ? (
            <p>Todavía no hay campañas de Snov de esta marca. Creala en Snov y actualizá Emails.</p>
          ) : (
            <ul className="space-y-1">
              {snov.campanas.map((c) => (
                <li key={c.snovId}>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={elegidas.includes(c.snovId)} disabled={!!c.deOtroEnvio || !!ocupado} onChange={() => alternar(c.snovId)} />
                    <span>
                      {c.titulo || `Campaña ${c.snovId}`} · {num(c.enviados)} {plural(c.enviados, "enviado", "enviados")}
                      {c.deOtroEnvio && <span className="text-slate-500"> · ya manda «{c.deOtroEnvio}»</span>}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          <button type="button" className={btnSecundario} disabled={!cambio || !!ocupado} onClick={() => void vincular()}>
            {ocupado === "vincular" ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Link2 size={15} aria-hidden="true" />}
            Vincular la campaña de Snov
          </button>
          {guardado && <p data-testid="snov-vinculada">Listo: con cada actualización de Emails, quien la recibe queda enviado en el padrón, con su hora.</p>}
        </div>
      )}
      {error && (
        <p role="alert" className="rounded-xl bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </p>
      )}
    </section>
  );
}
